import type { entityVersions } from '@clockwise/clients';
import {
  type CreateUserInput,
  EMPLOYEE_TYPES,
  type PatchUserInput,
  type User,
} from '@clockwise/clients/users';
import { ApiError, isResolvableCountryInput, LifecycleStatus } from '@clockwise/shared';
import { DatePicker } from '@clockwise/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Briefcase,
  DollarSign,
  Hash,
  Lock,
  MapPin,
  Phone,
  Settings2,
  User as UserIcon,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import {
  ApplyChangeDialog,
  savedMessage,
  shouldAsk,
} from '@/components/custom/apply-change-dialog';
import { BackLink } from '@/components/custom/back-link';
import { Combobox } from '@/components/custom/combobox';
import { COUNTRY_FIELD_ERROR, CountrySelectField } from '@/components/custom/country-select';
import { CustomFieldsEditor } from '@/components/custom/custom-fields-editor';
import {
  effectiveRange,
  useEntityVersions,
  withVersionValues,
} from '@/components/custom/entity-version-history';
import { LifecycleStatusBadge } from '@/components/custom/lifecycle-status-badge';
import { NoAccess } from '@/components/custom/no-access';
import { RequiredMark } from '@/components/custom/required-mark';
import { UserAutocomplete } from '@/components/custom/user-autocomplete';
import { UserCostChangeSheet } from '@/components/custom/user-cost-change-sheet';
import { PageShell } from '@/components/layout/page-shell';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SheetFooter } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import {
  departmentsApi,
  serviceRolesApi,
  systemRolesApi,
  userEmployeeCostsApi,
  usersApi,
} from '@/lib/api';
import { lastSearch } from '@/lib/last-search';
import { useHasPermissions } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';
import { fromSelectValue, SELECT_NONE, toSelectValue } from '@/lib/select-none';
import { useObjectCustomFields } from '@/lib/use-object-custom-fields';
import { listIanaTimezones, todayIso, userDisplayName, userStatus } from '@/lib/user-display';
import { cn } from '@/lib/utils';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

// Single source of truth for both create (/users/new) and edit
// (/users/$id/edit). Sections, validation, and field-error routing match
// across modes; the only differences are where data is seeded from, which
// API call submit makes, and edit-only chrome (cost section, back link to
// the detail page, status badge derived from a loaded user).

// Email format validation lives server-side (the API's CreateUserInput uses
// z.string().email()). Keeping a client-side .email() check here would also
// gate Save in edit mode on legacy rows whose stored email predates current
// validation — but the edit page renders email read-only, so the user has no
// way to fix it. Server returns a path='email' field error on bad input which
// the existing onError handler routes back to the form. Requiring non-empty
// is safe for both modes: edit mode's email input isn't registered with the
// form (it's a static read-only display of user.email, always non-empty for
// an existing user), so this only gates the create-mode input.
const UserEditSchema = z.object({
  email: z.string().min(1, 'Required'),
  firstName: z.string().min(1, 'Required').max(120),
  lastName: z.string().min(1, 'Required').max(120),
  preferredName: z.string().max(240).optional(),
  title: z.string().max(120).optional(),
  systemRoleId: z.string().uuid('Required'),
  serviceRoleId: z.string().uuid('Required'),
  managerId: z.string().uuid().nullable(),
  srControllerId: z.string().uuid().nullable(),
  departmentId: z.string().uuid().nullable(),
  employeeType: z.enum([...EMPLOYEE_TYPES, '']).optional(),
  startDate: z.string().optional(),
  terminatedDate: z.string().optional(),
  hoursPerMonth: z.string().optional(),
  billRate: z.string().optional(),
  active: z.boolean(),
  workPhone: z.string().max(40).optional(),
  homePhone: z.string().max(40).optional(),
  cellPhone: z.string().max(40).optional(),
  addressLine1: z.string().max(200).optional(),
  addressLine2: z.string().max(200).optional(),
  city: z.string().max(120).optional(),
  state: z.string().max(120).optional(),
  zip: z.string().max(40).optional(),
  // Stored as an ISO-3166 alpha-2 code. The select only emits codes; the
  // refine exists for legacy rows whose free-text value doesn't resolve —
  // it blocks submit (with an inline error) instead of silently keeping or
  // clearing the unrecognized value.
  country: z.string().max(120).optional().refine(isResolvableCountryInput, COUNTRY_FIELD_ERROR),
  location: z.string().max(200).optional(),
  historicalTimesheetId: z.string().max(120).optional(),
  kekaEmployeeId: z.string().trim().min(1, 'Required').max(120),
  quickbooksId: z.string().max(120).optional(),
  timezone: z.string().min(1).max(64).optional(),
});
type UserEditValues = z.infer<typeof UserEditSchema>;

const FIELD_NAMES = [
  'email',
  'firstName',
  'lastName',
  'preferredName',
  'title',
  'systemRoleId',
  'serviceRoleId',
  'managerId',
  'srControllerId',
  'departmentId',
  'employeeType',
  'startDate',
  'terminatedDate',
  'hoursPerMonth',
  'billRate',
  'active',
  'workPhone',
  'homePhone',
  'cellPhone',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'zip',
  'country',
  'location',
  'historicalTimesheetId',
  'kekaEmployeeId',
  'quickbooksId',
  'timezone',
] as const;
type EditFieldName = (typeof FIELD_NAMES)[number];
function isEditField(s: string): s is EditFieldName {
  return (FIELD_NAMES as readonly string[]).includes(s);
}

const EMPTY_DEFAULTS: UserEditValues = {
  email: '',
  firstName: '',
  lastName: '',
  preferredName: '',
  title: '',
  systemRoleId: '',
  serviceRoleId: '',
  managerId: null,
  srControllerId: null,
  departmentId: null,
  employeeType: '',
  startDate: '',
  terminatedDate: '',
  hoursPerMonth: '',
  billRate: '',
  active: true,
  workPhone: '',
  homePhone: '',
  cellPhone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  location: '',
  historicalTimesheetId: '',
  kekaEmployeeId: '',
  quickbooksId: '',
  // IST default for newly-added employees — every active tenant runs on
  // Calcutta time today. The Settings → Timezone Combobox lets the admin
  // pick a different IANA zone if needed.
  timezone: 'Asia/Calcutta',
};

export type UserEditFormMode = 'create' | 'edit';

interface Props {
  mode: UserEditFormMode;
  /** Required in edit mode; ignored in create mode. */
  userId: string | null;
  /**
   * Amend what a PAST version of this employee says, in place (D10), instead of
   * editing the record. Same form, same validation — seeded from what that
   * version says and submitted to the amend endpoint. Ignored in create mode.
   *
   * A dialog of its own was the alternative and is the wrong one: it would
   * re-implement this field set, the custom-fields editor, the field-level API
   * error mapping and the unsaved-changes guard, and then drift from them.
   *
   * Always 'page' — `UserFormSheet` never passes this, so amending a past
   * version stays a deliberate full-page destination, the same way it does
   * for customers (`customers.$id.edit.tsx`).
   */
  amendVersion?: number;
  /**
   * `'page'` (default): full-page chrome — `PageShell`, a "Back to..." link,
   * Cancel navigates away.
   *
   * `'sheet'`: lean chrome for use inside `UserFormSheet` — no `PageShell`/
   * back link, Cancel and every other dismissal (Escape, overlay click, the
   * sheet's own X) route through the unsaved-changes guard, and a
   * successful (non-amend) save closes the sheet instead of navigating.
   * Requires `open`/`onOpenChange`.
   */
  variant?: 'page' | 'sheet';
  /** The Sheet's own open state — see `variant`. Ignored when `variant`
   *  is `'page'`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `'sheet'` only: the outer `<Sheet>` lives in `UserFormSheet`, one level
   *  up, so ESC/overlay/X can't call the guard's `requestOpenChange` directly
   *  — this ref hands it up so `UserFormSheet` can wire it to `onOpenChange`. */
  guardRef?: React.MutableRefObject<(open: boolean) => void>;
}

export function UserEditForm({
  mode,
  userId,
  amendVersion,
  variant = 'page',
  open = false,
  onOpenChange,
  guardRef,
}: Props) {
  const isCreate = mode === 'create';
  const isSheet = variant === 'sheet';
  const amending_ = !isCreate && amendVersion != null;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Edit mode loads the existing row; create mode skips the fetch and
  // renders straight from EMPTY_DEFAULTS.
  const detail = useQuery({
    queryKey: queryKeys.users.detail(userId ?? ''),
    queryFn: () => usersApi.get(userId as string),
    enabled: !isCreate && !!userId,
  });
  const user = detail.data?.data ?? null;

  // Only fetched when amending — this endpoint diffs every version and resolves
  // FK labels server-side, so an ordinary edit must not pay for it.
  const versionsQuery = useEntityVersions('user', userId ?? '', { enabled: amending_ });
  const amending = amending_
    ? (versionsQuery.data?.data.find((v) => v.version === amendVersion) ?? null)
    : null;
  // Until the version resolves, the form would hold the LIVE record's values
  // while the submit path writes them to a past version — today's data silently
  // becoming what a closed period reports. Nothing renders until it settles.
  const amendNotReady = amending_ && !amending;

  const systemRoles = useQuery({
    queryKey: queryKeys.systemRoles.list({
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 100,
      offset: 0,
    }),
    queryFn: () =>
      systemRolesApi.list({
        active: true,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 100,
        offset: 0,
      }),
  });
  const serviceRoles = useQuery({
    queryKey: queryKeys.serviceRoles.list({
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 100,
      offset: 0,
    }),
    queryFn: () =>
      serviceRolesApi.list({
        active: true,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 100,
        offset: 0,
      }),
  });
  const departments = useQuery({
    queryKey: queryKeys.departments.list({
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 200,
      offset: 0,
    }),
    queryFn: () =>
      departmentsApi.list({
        active: true,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 200,
        offset: 0,
      }),
  });
  // These option lists are still capped fetches (Category B follow-up to
  // convert to lazy autocompletes). Warn loudly in dev if a tenant outgrows
  // the cap so the missing-option bug surfaces before it ships.
  warnIfTruncated(systemRoles.data, 'user-edit system-role select');
  warnIfTruncated(serviceRoles.data, 'user-edit service-role select');
  warnIfTruncated(departments.data, 'user-edit department select');

  const form = useForm<UserEditValues>({
    resolver: zodResolver(UserEditSchema),
    defaultValues: EMPTY_DEFAULTS,
  });

  // Gate the edit form's first paint until the loaded row has been seeded
  // into RHF. The role/department/employee-type pickers are controlled
  // Radix <Select>s: if they mount on EMPTY_DEFAULTS and then receive their
  // real value from the post-mount reset(), the controlled Select emits a
  // spurious onValueChange('') that writes the empty value straight back,
  // wiping the seed. Mounting them only after reset() — with their option
  // catalogs already loaded so the value has a matching item — sidesteps the
  // transition entirely. Create mode starts seeded (renders from defaults).
  const [seeded, setSeeded] = useState(isCreate);

  // Seeded from the VERSION when amending: `customFields` is a versioned column
  // and rides along in the patch body, so seeding it from the live record would
  // overwrite whatever that version recorded the moment anyone amends any other
  // field.
  const cf = useObjectCustomFields(
    'user',
    amending
      ? (amending.values.customFields as NonNullable<typeof user>['customFields'] | undefined)
      : user?.customFields,
  );

  // Default the create form's role pickers to the first active role
  // once the catalogs load — matches the old Add-user sheet behavior
  // so a save doesn't 400 on "Required". The reset is guarded so we
  // don't clobber what the user has typed.
  useEffect(() => {
    if (!isCreate) return;
    const sys = systemRoles.data?.data[0]?.id ?? '';
    const svc = serviceRoles.data?.data[0]?.id ?? '';
    if (!sys && !svc) return;
    if (form.getValues('systemRoleId') === '' && sys) {
      form.setValue('systemRoleId', sys, { shouldValidate: false });
    }
    if (form.getValues('serviceRoleId') === '' && svc) {
      form.setValue('serviceRoleId', svc, { shouldValidate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreate, systemRoles.data?.data[0]?.id, serviceRoles.data?.data[0]?.id]);

  // Edit mode seeds the form from the loaded user; resets on row identity
  // change so navigating between users in the same mount slot still works.
  useEffect(() => {
    if (isCreate || !user) return;
    if (amendNotReady) return;
    const source = amending ? withVersionValues(user, amending, () => ({})) : user;
    form.reset({
      email: source.email,
      firstName: source.firstName,
      lastName: source.lastName,
      preferredName: source.preferredName ?? '',
      title: source.title ?? '',
      systemRoleId: source.systemRoleId,
      serviceRoleId: source.serviceRoleId,
      managerId: source.managerId,
      srControllerId: source.srControllerId,
      departmentId: source.departmentId,
      employeeType: source.employeeType ?? '',
      startDate: source.startDate ?? '',
      terminatedDate: source.terminatedDate ?? '',
      hoursPerMonth: source.hoursPerMonth ?? '',
      billRate: source.billRate ?? '',
      active: source.active,
      workPhone: source.workPhone ?? '',
      homePhone: source.homePhone ?? '',
      cellPhone: source.cellPhone ?? '',
      addressLine1: source.addressLine1 ?? '',
      addressLine2: source.addressLine2 ?? '',
      city: source.city ?? '',
      state: source.state ?? '',
      zip: source.zip ?? '',
      country: source.country ?? '',
      location: source.location ?? '',
      historicalTimesheetId: source.historicalTimesheetId ?? '',
      kekaEmployeeId: source.kekaEmployeeId ?? '',
      quickbooksId: source.quickbooksId ?? '',
      timezone: source.timezone,
    });
    setSeeded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `amending?.version` is load-bearing, not decorative: the effect bails out
    // above while the version is still resolving, so without it here the effect
    // never re-runs once the version lands and the form stays a skeleton for
    // good. Only reachable on a cold load — arriving from the detail page warms
    // both caches, which is exactly why testing by clicking through missed it.
  }, [isCreate, user?.id, user?.updatedAt, amending?.version]);

  // Compute the derived Status badge from the form's live values so it
  // updates as the user types start/term dates and toggles `active`.
  const liveStatus = userStatus({
    active: form.watch('active'),
    startDate: emptyToNull(form.watch('startDate')),
    terminatedDate: emptyToNull(form.watch('terminatedDate')),
  });

  const timezones = useMemo(() => listIanaTimezones(), []);

  // Cost is a separate permission surface — gate the section + the
  // query so a user.view-only role never even sees that cost data
  // exists for this user. In create mode there's no user yet, so the
  // section is suppressed unconditionally.
  const {
    'user.cost.view': canViewCost,
    'user.cost.edit': canEditCost,
    'user.pii.view': canViewPii,
    'user.admin': canCorrectHistory,
  } = useHasPermissions(['user.cost.view', 'user.cost.edit', 'user.pii.view', 'user.admin']);
  const showCostSection = !isCreate && canViewCost;
  // Address (PII) is gated by user.pii.view. This admin form always edits
  // ANOTHER employee (self-service goes through PATCH /me), so there's no self
  // override here — matching the backend write guard. When locked we hide the
  // inputs AND omit the address fields from the payload, so a save can't clobber
  // an address the editor can't see (and the server would 403 on it anyway).

  // The cost period IN EFFECT TODAY — the live baseline shown in the
  // "Currently $X effective Y" header and prefilled into the Change-cost
  // sheet. Querying with asOf=today rather than "first row with
  // effectiveTo===null" so a future-scheduled change does NOT shadow
  // the cost that actually applies right now.
  const today = todayIso();
  const costInEffect = useQuery({
    queryKey: queryKeys.userEmployeeCosts.inEffect(userId ?? '', today),
    queryFn: () => userEmployeeCostsApi.list({ userId: userId as string, asOf: today }),
    enabled: showCostSection && !!userId,
  });
  const currentCost = costInEffect.data?.data[0] ?? null;
  const [costSheetOpen, setCostSheetOpen] = useState(false);

  // Edit: email is identity, not patchable. Everything else mirrors create.
  // Shared by the save and its "When did this become true?" preview, so the
  // question is asked about exactly what would be written.
  const editPayload = (values: UserEditValues): PatchUserInput => ({
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    preferredName: blank(values.preferredName),
    title: blank(values.title),
    systemRoleId: values.systemRoleId,
    serviceRoleId: values.serviceRoleId,
    managerId: values.managerId,
    srControllerId: values.srControllerId,
    departmentId: values.departmentId,
    employeeType: values.employeeType ? values.employeeType : null,
    startDate: blank(values.startDate),
    terminatedDate: blank(values.terminatedDate),
    hoursPerMonth: emptyNumber(values.hoursPerMonth),
    billRate: emptyNumber(values.billRate),
    active: values.active,
    workPhone: blank(values.workPhone),
    homePhone: blank(values.homePhone),
    cellPhone: blank(values.cellPhone),
    // Address fields are sent only when the caller holds user.pii.view —
    // omitted otherwise so the patch leaves the (unseen) address untouched.
    ...(canViewPii ? addressPayload(values) : {}),
    historicalTimesheetId: blank(values.historicalTimesheetId),
    // Required — schema guarantees a non-empty trimmed value.
    kekaEmployeeId: values.kekaEmployeeId.trim(),
    quickbooksId: blank(values.quickbooksId),
    timezone: values.timezone || undefined,
    customFields: cf.values,
  });

  /** An edit waiting on its answer to "When did this become true?". */
  const [question, setQuestion] = useState<{
    values: UserEditValues;
    impact: entityVersions.EditImpact;
  } | null>(null);
  const [checking, setChecking] = useState(false);

  // Shared by the save and its preview: the preview runs the save's own
  // checks, so a field error from either lands on the same field.
  const handleSaveError = (err: unknown) => {
    setQuestion(null);
    const hadCfErrors = cf.handleApiError(err);
    if (err instanceof ApiError && err.fields?.length) {
      for (const fe of err.fields) {
        if (fe.path.startsWith('customFields.')) continue;
        if (isEditField(fe.path)) form.setError(fe.path, { message: fe.message });
      }
      toast.error('Please fix the highlighted fields');
      return;
    }
    if (hadCfErrors) {
      toast.error('Please fix the highlighted fields');
      return;
    }
    toast.error(err instanceof Error ? err.message : 'Save failed');
  };

  const mutation = useMutation({
    mutationFn: async ({
      values,
      applyTo,
    }: {
      values: UserEditValues;
      applyTo?: entityVersions.ApplyTo;
      impact?: entityVersions.EditImpact;
    }): Promise<{ data: User }> => {
      if (isCreate) {
        const payload: CreateUserInput = {
          email: values.email.trim().toLowerCase(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          preferredName: blank(values.preferredName),
          title: blank(values.title),
          systemRoleId: values.systemRoleId,
          serviceRoleId: values.serviceRoleId,
          managerId: values.managerId,
          srControllerId: values.srControllerId,
          departmentId: values.departmentId,
          employeeType: values.employeeType ? values.employeeType : null,
          startDate: blank(values.startDate),
          terminatedDate: blank(values.terminatedDate),
          hoursPerMonth: emptyNumber(values.hoursPerMonth),
          billRate: emptyNumber(values.billRate),
          active: values.active,
          workPhone: blank(values.workPhone),
          homePhone: blank(values.homePhone),
          cellPhone: blank(values.cellPhone),
          // Address fields are sent only when the caller holds user.pii.view —
          // omitted entirely otherwise (the section is hidden too).
          ...(canViewPii ? addressPayload(values) : {}),
          historicalTimesheetId: blank(values.historicalTimesheetId),
          // Required — schema guarantees a non-empty trimmed value.
          kekaEmployeeId: values.kekaEmployeeId.trim(),
          quickbooksId: blank(values.quickbooksId),
          timezone: values.timezone || undefined,
          customFields: cf.values,
        };
        return usersApi.create(payload);
      }
      const payload = editPayload(values);
      if (amendVersion != null) {
        // Deliberately widened: this mutation now returns either a saved user
        // or an amend result, and `onSuccess` branches on the same flag.
        return (await usersApi.amendVersion(userId as string, amendVersion, payload)) as never;
      }
      return usersApi.patch(userId as string, payload, applyTo);
    },
    onSuccess: (res, vars) => {
      setQuestion(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      if (amendVersion != null) {
        // An amend touches ONE version and never ripples, so the versions still
        // carrying the replaced value are named rather than quietly rewritten.
        const later =
          (res as { data?: { laterVersionsStillHolding?: { version: number }[] } }).data
            ?.laterVersionsStillHolding ?? [];
        toast.success(
          later.length === 0
            ? 'Correction saved'
            : later.length === 1
              ? 'Correction saved. 1 later period still shows the old value. Open it to change that too.'
              : `Correction saved. ${later.length} later periods still show the old value. Open each to change it too.`,
          later.length > 0 ? { duration: 10000 } : undefined,
        );
        navigate({
          to: '/users/$id',
          params: { id: userId as string },
          search: { version: amendVersion },
        });
        return;
      }
      toast.success(
        isCreate
          ? 'Employee created'
          : vars.impact
            ? savedMessage(
                vars.impact,
                vars.applyTo ?? 'today',
                (res as { meta?: entityVersions.PatchMeta }).meta?.restated,
              )
            : 'Employee saved',
      );
      // A termination (or deactivation) closes this employee's customer-team
      // memberships server-side, so the detail page's Customers section must
      // refetch. Prefix-invalidate the whole namespace to cover both the
      // "today" and "show ended" (1900→2999) date-range cache entries.
      queryClient.invalidateQueries({ queryKey: queryKeys.customerTeam.all });
      if (isSheet) {
        // Opened from the list (create) or the detail page (edit) — either
        // way the host page owns its own query and refetches from the
        // invalidation above; this only needs to close the panel, not go
        // anywhere. Edit additionally invalidates this row's own detail
        // query, since `users.all` alone wouldn't cover a caller already
        // sitting on the detail page for this exact employee.
        if (!isCreate) {
          queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId as string) });
        }
        onOpenChange?.(false);
        return;
      }
      navigate({ to: '/users/$id', params: { id: isCreate ? res.data.id : (userId as string) } });
    },
    onError: (err) => handleSaveError(err),
  });

  const onSubmit = form.handleSubmit(async (v) => {
    cf.reset();
    // Only an edit that changes what logged timesheets report is asked
    // "When did this become true?" — and only of someone allowed to answer
    // "always". Everyone else's edit applies from today, as it always has.
    if (isCreate || amendVersion != null || !canCorrectHistory) {
      mutation.mutate({ values: v });
      return;
    }
    setChecking(true);
    try {
      const { data: impact } = await usersApi.previewPatch(userId as string, editPayload(v));
      if (shouldAsk(impact)) {
        setQuestion({ values: v, impact });
        return;
      }
    } catch (err) {
      handleSaveError(err);
      return;
    } finally {
      setChecking(false);
    }
    mutation.mutate({ values: v });
  });
  const errors = form.formState.errors;

  // Only load-bearing in sheet mode — `open`/`onOpenChange` default to
  // `false`/undefined in page mode, where nothing calls `requestOpenChange`
  // or renders `discardDialog`, so the guard stays inert.
  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    onOpenChange: onOpenChange ?? (() => {}),
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
  });
  if (guardRef) guardRef.current = requestOpenChange;

  // Edit mode waits for the row to load AND be seeded into the form before
  // rendering, so the controlled Selects mount on real values rather than
  // empty defaults (see the `seeded` note above). `optionsReady` ensures the
  // picker catalogs are present so the seeded value has a matching item.
  const optionsReady = Boolean(systemRoles.data && serviceRoles.data && departments.data);
  const showSkeleton = !isCreate && (detail.isLoading || !user || !optionsReady || !seeded);

  // Nothing safe to amend if the history could not be read, or if the version
  // in the URL is gone — say so rather than rendering a form seeded from the
  // live record that would write today's values into a closed period.
  if (amending_ && versionsQuery.isError) {
    return (
      <PageShell>
        <p className="text-sm text-destructive">
          Could not load this record's history, so there is nothing safe to correct. Reload the page
          to try again.
        </p>
      </PageShell>
    );
  }
  if (amending_ && !versionsQuery.isLoading && !amending) {
    return (
      <PageShell>
        <p className="text-sm text-destructive">This period no longer exists on this record.</p>
      </PageShell>
    );
  }

  // Every Edit affordance pointing here — the list pencils and the detail
  // page's button — gates on PRESENCE of `user.edit`, which is not scope-aware.
  // A caller whose view scope is wider than their edit scope can therefore
  // reach this route for an employee they cannot save. `related.editable` is
  // the server's scope-aware verdict (UserAccessService.canEdit); refuse on it
  // before the form seeds, rather than letting someone fill the whole thing in
  // and lose the work to a 403 on submit.
  //
  // Ordinary edits only. Amending is gated above by `user.admin`, a separate
  // and higher authority that doesn't imply `user.edit` — leave that path as is.
  if (!isCreate && !amending_ && detail.isSuccess && !detail.data.related.editable) {
    const noAccess = <NoAccess permission="user.edit" resource="this employee" />;
    return isSheet ? (
      <div className="flex-1 p-4">{noAccess}</div>
    ) : (
      <PageShell>{noAccess}</PageShell>
    );
  }

  const Wrapper = isSheet ? SheetFormBody : PageShell;

  const submitLabel =
    mutation.isPending || checking
      ? isCreate
        ? 'Creating…'
        : 'Saving…'
      : isCreate
        ? 'Create employee'
        : amendVersion != null
          ? 'Save correction'
          : 'Save';

  // Everything above the field list — the back link (page mode only), the
  // amend banner, the title/description/status-badge header. Pinned in sheet
  // mode (`shrink-0 border-b`, outside the scrolling body below) so it reads
  // the same way `CustomerFormSheet`'s `SheetHeader` does; `contents` in page
  // mode means this wrapper div doesn't affect that layout at all — the
  // children flow exactly as they did before this was introduced.
  const headerZone = (
    <div className={isSheet ? 'shrink-0 space-y-3 border-b p-4' : 'contents'}>
      {isSheet ? null : (
        <PageShell className="gap-6 overflow-y-auto">
          <div>
            {isCreate ? (
              <BackLink to="/users">Back to Employees</BackLink>
            ) : (
              <BackLink to="/users/$id" params={{ id: userId as string }}>
                Back to Employee
              </BackLink>
            )}
          </div>
        </PageShell>
      )}

      {/* Amending is not editing, and the page has to say so before anyone
          types: these values are what a past period reports, so saving restates
          every report over that period rather than changing the record. */}
      {amending ? (
        <div className="max-w-2xl rounded-md border border-amber-500/50 bg-amber-50 px-3 py-2 text-sm dark:bg-amber-950/30">
          <p>
            You're changing what was true for {user ? userDisplayName(user) : 'this employee'}{' '}
            during <strong>{effectiveRange(amending)}</strong>. Every report covering those dates
            will show the new values.
          </p>
          <p className="mt-1 text-muted-foreground">
            {user ? userDisplayName(user) : 'This employee'}'s current details and later periods
            keep their values.
          </p>
        </div>
      ) : null}

      <header className="space-y-1">
        <h1 className="flex items-center gap-3 text-2xl font-semibold">
          {isCreate ? (
            <span>Add Employee</span>
          ) : detail.isLoading ? (
            <Skeleton className="h-7 w-56" />
          ) : user ? (
            <>
              <span>
                {amending ? 'Correct past details · ' : 'Edit '}
                {userDisplayName(user)}
              </span>
              <LifecycleStatusBadge
                status={liveStatus === 'Active' ? LifecycleStatus.Active : LifecycleStatus.Inactive}
              />
            </>
          ) : null}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isCreate
            ? 'Create an employee. Email is set here and is not editable after create.'
            : 'Email is set at create time and not editable here. Status is derived from the active flag plus start & termination dates.'}
        </p>
      </header>
    </div>
  );

  return (
    <Wrapper className={isSheet ? undefined : 'gap-6 overflow-y-auto'}>
      {headerZone}

      {!isCreate && detail.isError ? (
        <p className={isSheet ? 'p-4 text-sm text-destructive' : 'text-sm text-destructive'}>
          {detail.error instanceof ApiError ? detail.error.message : 'Failed to load employee.'}
        </p>
      ) : showSkeleton ? (
        <div className={isSheet ? 'space-y-3 p-4' : 'space-y-3'}>
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className={isSheet ? 'flex min-h-0 flex-1 flex-col' : 'flex max-w-3xl flex-col gap-8'}
        >
          {/* Sheet mode: this is the ONLY scrolling region — header above and
              the Save/Cancel row below both stay put. Page mode: `contents`
              keeps every section a direct flex child of the form above, so
              its own `gap-8`/`max-w-3xl` still governs — unchanged from
              before this split. */}
          <div className={isSheet ? 'min-h-0 flex-1 space-y-8 overflow-y-auto p-4' : 'contents'}>
            <Accordion
              type="multiple"
              defaultValue={[
                'identity',
                'employment',
                'employee-cost',
                'contact',
                'address',
                'external-ids',
                'settings',
              ]}
            >
              <AccordionItem value="identity">
                <AccordionTrigger>
                  <div className="flex items-center gap-3">
                    <UserIcon className="size-4 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Identity</div>
                      <div className="text-xs text-muted-foreground">
                        Display name and title. Preferred name appears in pickers and lists.
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow
                      id="firstName"
                      label="First name"
                      placeholder={isCreate ? 'Alice' : undefined}
                      register={form.register('firstName')}
                      error={errors.firstName?.message}
                      required
                    />
                    <FieldRow
                      id="lastName"
                      label="Last name"
                      placeholder={isCreate ? 'Smith' : undefined}
                      register={form.register('lastName')}
                      error={errors.lastName?.message}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow
                      id="preferredName"
                      label="Preferred name"
                      placeholder={user ? `${user.firstName} ${user.lastName}`.trim() : undefined}
                      register={form.register('preferredName')}
                      error={errors.preferredName?.message}
                    />
                    <FieldRow
                      id="title"
                      label="Title"
                      register={form.register('title')}
                      error={errors.title?.message}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">
                      Email
                      <RequiredMark />
                    </Label>
                    {isCreate ? (
                      <Input
                        id="email"
                        type="email"
                        placeholder="alice@company.com"
                        aria-invalid={Boolean(errors.email?.message)}
                        aria-required
                        {...form.register('email')}
                      />
                    ) : (
                      <Input
                        id="email"
                        value={user?.email ?? ''}
                        readOnly
                        disabled
                        className="bg-muted/30"
                      />
                    )}
                    {errors.email?.message ? (
                      <p className="text-xs text-destructive">{errors.email.message}</p>
                    ) : null}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="employment">
                <AccordionTrigger>
                  <div className="flex items-center gap-3">
                    <Briefcase className="size-4 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Employment</div>
                      <div className="text-xs text-muted-foreground">
                        Role, reporting, capacity, pricing, lifecycle.
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <SelectField
                      id="systemRoleId"
                      label="System role"
                      value={form.watch('systemRoleId')}
                      onChange={(v) => form.setValue('systemRoleId', v, { shouldValidate: true })}
                      options={systemRoles.data?.data ?? []}
                      error={errors.systemRoleId?.message}
                      required
                    />
                    <SelectField
                      id="serviceRoleId"
                      label="Service role"
                      value={form.watch('serviceRoleId')}
                      onChange={(v) => form.setValue('serviceRoleId', v, { shouldValidate: true })}
                      options={serviceRoles.data?.data ?? []}
                      error={errors.serviceRoleId?.message}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="departmentId">Department</Label>
                      <Select
                        value={toSelectValue(form.watch('departmentId'))}
                        onValueChange={(v) =>
                          form.setValue('departmentId', fromSelectValue(v), {
                            shouldValidate: true,
                          })
                        }
                      >
                        <SelectTrigger id="departmentId" className="w-full">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SELECT_NONE}>— None —</SelectItem>
                          {(departments.data?.data ?? []).map((d) => (
                            <SelectItem key={d.id} value={d.id}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.departmentId?.message ? (
                        <p className="text-xs text-destructive">{errors.departmentId.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Manager</Label>
                      <UserAutocomplete
                        value={form.watch('managerId')}
                        excludeIds={!isCreate && userId ? [userId] : undefined}
                        onChange={(u) =>
                          form.setValue('managerId', u?.id ?? null, { shouldValidate: true })
                        }
                      />
                      {errors.managerId?.message ? (
                        <p className="text-xs text-destructive">{errors.managerId.message}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>SR Controller</Label>
                      <UserAutocomplete
                        value={form.watch('srControllerId')}
                        srControllerCandidates={{ includeId: !isCreate && userId ? userId : null }}
                        onChange={(u) =>
                          form.setValue('srControllerId', u?.id ?? null, { shouldValidate: true })
                        }
                      />
                      {errors.srControllerId?.message ? (
                        <p className="text-xs text-destructive">{errors.srControllerId.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="employeeType">Employee type</Label>
                      <Select
                        value={form.watch('employeeType') || SELECT_NONE}
                        onValueChange={(v) =>
                          form.setValue(
                            'employeeType',
                            v === SELECT_NONE ? '' : (v as (typeof EMPLOYEE_TYPES)[number]),
                            { shouldValidate: true },
                          )
                        }
                      >
                        <SelectTrigger id="employeeType" className="w-full">
                          <SelectValue placeholder="None" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SELECT_NONE}>— None —</SelectItem>
                          {EMPLOYEE_TYPES.map((t) => (
                            <SelectItem key={t} value={t}>
                              {t}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.employeeType?.message ? (
                        <p className="text-xs text-destructive">{errors.employeeType.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="active">Active</Label>
                      <Select
                        value={form.watch('active') ? 'active' : 'inactive'}
                        onValueChange={(v) =>
                          form.setValue('active', v === 'active', { shouldDirty: true })
                        }
                      >
                        <SelectTrigger id="active" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="startDate">Start date</Label>
                      <DatePicker
                        id="startDate"
                        value={form.watch('startDate') ?? ''}
                        onChange={(v) =>
                          form.setValue('startDate', v, { shouldValidate: true, shouldDirty: true })
                        }
                        clearable
                        ariaInvalid={!!errors.startDate}
                      />
                      {errors.startDate ? (
                        <p className="text-xs text-destructive">{errors.startDate.message}</p>
                      ) : null}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="terminatedDate">Terminated date</Label>
                      <DatePicker
                        id="terminatedDate"
                        value={form.watch('terminatedDate') ?? ''}
                        onChange={(v) =>
                          form.setValue('terminatedDate', v, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        clearable
                        ariaInvalid={!!errors.terminatedDate}
                      />
                      {errors.terminatedDate ? (
                        <p className="text-xs text-destructive">{errors.terminatedDate.message}</p>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow
                      id="hoursPerMonth"
                      label="Available hours / month"
                      type="number"
                      step="0.01"
                      placeholder="173.33"
                      register={form.register('hoursPerMonth')}
                      error={errors.hoursPerMonth?.message}
                    />
                    <FieldRow
                      id="billRate"
                      label="Bill rate (USD / hour)"
                      type="number"
                      step="0.01"
                      register={form.register('billRate')}
                      error={errors.billRate?.message}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {showCostSection ? (
                <AccordionItem value="employee-cost">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <DollarSign className="size-4 shrink-0 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-semibold">Employee cost</div>
                        <div className="text-xs text-muted-foreground">
                          Versioned — each change records a new effective period.
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex items-center justify-between gap-3">
                      <div className="space-y-0.5">
                        <p className="text-sm">
                          {currentCost ? (
                            <>
                              <span className="font-semibold">
                                ${currentCost.costPerHour} / hour
                              </span>
                              <span className="ml-2 text-xs text-muted-foreground">
                                effective {currentCost.effectiveFrom}
                              </span>
                            </>
                          ) : (
                            <span className="text-muted-foreground">No cost recorded yet</span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          History lives on the view page. Use Change cost to record a new period
                          (retroactive or scheduled).
                        </p>
                      </div>
                      {canEditCost ? (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setCostSheetOpen(true)}
                          disabled={costInEffect.isLoading}
                        >
                          Change cost
                        </Button>
                      ) : null}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ) : null}

              <AccordionItem value="contact">
                <AccordionTrigger>
                  <div className="flex items-center gap-3">
                    <Phone className="size-4 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Contact</div>
                      <div className="text-xs text-muted-foreground">Phones — all optional.</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <FieldRow
                      id="workPhone"
                      label="Work phone"
                      register={form.register('workPhone')}
                      error={errors.workPhone?.message}
                    />
                    <FieldRow
                      id="homePhone"
                      label="Home phone"
                      register={form.register('homePhone')}
                      error={errors.homePhone?.message}
                    />
                    <FieldRow
                      id="cellPhone"
                      label="Cell phone"
                      register={form.register('cellPhone')}
                      error={errors.cellPhone?.message}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>

              {canViewPii ? (
                <AccordionItem value="address">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <MapPin className="size-4 shrink-0 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-semibold">Address</div>
                        <div className="text-xs text-muted-foreground">
                          Flat address. Location is a free-text label that may differ from
                          country/state.
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-3">
                    <FieldRow
                      id="addressLine1"
                      label="Address line 1"
                      register={form.register('addressLine1')}
                      error={errors.addressLine1?.message}
                    />
                    <FieldRow
                      id="addressLine2"
                      label="Address line 2"
                      register={form.register('addressLine2')}
                      error={errors.addressLine2?.message}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <FieldRow
                        id="city"
                        label="City"
                        register={form.register('city')}
                        error={errors.city?.message}
                      />
                      <FieldRow
                        id="state"
                        label="State / Province"
                        register={form.register('state')}
                        error={errors.state?.message}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FieldRow
                        id="zip"
                        label="Zip / Postal code"
                        register={form.register('zip')}
                        error={errors.zip?.message}
                      />
                      <CountrySelectField form={form} name="country" id="country" />
                    </div>
                    <FieldRow
                      id="location"
                      label="Location (display label)"
                      register={form.register('location')}
                      error={errors.location?.message}
                    />
                  </AccordionContent>
                </AccordionItem>
              ) : (
                // Locked — the editor lacks user.pii.view. Inputs are hidden and the
                // address fields are omitted from the submit payload, so a save can't
                // overwrite the address this editor can't see.
                <AccordionItem value="address">
                  <AccordionTrigger>
                    <div className="flex items-center gap-3">
                      <MapPin className="size-4 shrink-0 text-muted-foreground" />
                      <div className="text-sm font-semibold">Address</div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Lock aria-hidden />
                      <span>
                        Address restricted — you don't have access to this employee's PII.
                      </span>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              )}

              <AccordionItem value="external-ids">
                <AccordionTrigger>
                  <div className="flex items-center gap-3">
                    <Hash className="size-4 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">External IDs</div>
                      <div className="text-xs text-muted-foreground">
                        Free-text correlation IDs. Each is unique per tenant where present.
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FieldRow
                      id="historicalTimesheetId"
                      label="Historical timesheet ID"
                      register={form.register('historicalTimesheetId')}
                      error={errors.historicalTimesheetId?.message}
                    />
                    <FieldRow
                      id="kekaEmployeeId"
                      label="Keka employee ID"
                      register={form.register('kekaEmployeeId')}
                      error={errors.kekaEmployeeId?.message}
                      required
                    />
                  </div>
                  <FieldRow
                    id="quickbooksId"
                    label="Quickbooks ID"
                    register={form.register('quickbooksId')}
                    error={errors.quickbooksId?.message}
                  />
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="settings">
                <AccordionTrigger>
                  <div className="flex items-center gap-3">
                    <Settings2 className="size-4 shrink-0 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-semibold">Settings</div>
                      <div className="text-xs text-muted-foreground">
                        IANA timezone drives midnight-split for timers.
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-1.5">
                    <Label htmlFor="timezone">Timezone</Label>
                    <Combobox
                      id="timezone"
                      items={timezones}
                      value={form.watch('timezone') ?? null}
                      onChange={(v) =>
                        form.setValue('timezone', v ?? '', {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      getId={(z) => z}
                      getLabel={(z) => z}
                      filterFn={(z, q) => z.toLowerCase().includes(q.toLowerCase())}
                      placeholder="UTC"
                      hasError={Boolean(errors.timezone?.message)}
                    />
                    {errors.timezone?.message ? (
                      <p className="text-xs text-destructive">{errors.timezone.message}</p>
                    ) : null}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <CustomFieldsEditor {...cf.EditorProps} disabled={mutation.isPending} />
          </div>

          {isSheet ? (
            // Same pinned, right-aligned Cancel → primary footer as
            // `CustomerFormSheet`, so the two slide-overs read as one product.
            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => requestOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending || checking}>
                {submitLabel}
              </Button>
            </SheetFooter>
          ) : (
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={mutation.isPending || checking}>
                {submitLabel}
              </Button>
              <Button type="button" variant="outline" disabled={mutation.isPending} asChild>
                {isCreate ? (
                  <Link to="/users" search={lastSearch('/users')}>
                    Cancel
                  </Link>
                ) : (
                  <Link
                    to="/users/$id"
                    params={{ id: userId as string }}
                    search={lastSearch('/users/$id', { params: { id: userId as string } })}
                  >
                    Cancel
                  </Link>
                )}
              </Button>
            </div>
          )}
        </form>
      )}

      <ApplyChangeDialog
        open={question !== null}
        subject={user?.firstName ?? ''}
        subjectName={user ? userDisplayName(user) : ''}
        impact={question?.impact ?? null}
        pending={mutation.isPending}
        onCancel={() => setQuestion(null)}
        onConfirm={(applyTo) =>
          question && mutation.mutate({ values: question.values, applyTo, impact: question.impact })
        }
      />

      {showCostSection && userId ? (
        <UserCostChangeSheet
          userId={userId}
          currentCost={currentCost}
          open={costSheetOpen}
          onOpenChange={setCostSheetOpen}
        />
      ) : null}
      {isSheet ? discardDialog : null}
    </Wrapper>
  );
}

/** `variant: 'sheet'`'s stand-in for `PageShell` — the Sheet's own
 *  `SheetContent` already owns the fixed positioning and slide-in. This is
 *  deliberately NOT scrollable itself (no `overflow-y-auto`/padding of its
 *  own): the header and footer inside it are pinned, and only the middle
 *  section scrolls — see the `shrink-0`/`overflow-y-auto` split in the
 *  return below, the same header-stays-put structure `CustomerFormSheet`
 *  uses. A scrolling wrapper here would carry the header and footer along
 *  with the fields instead. */
function SheetFormBody({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex min-h-0 flex-1 flex-col', className)}>{children}</div>;
}

interface FieldRowProps {
  id: string;
  label: string;
  type?: string;
  step?: string;
  placeholder?: string;
  register: ReturnType<UseFormReturn<UserEditValues>['register']>;
  error?: string;
  required?: boolean;
}

function FieldRow({
  id,
  label,
  type = 'text',
  step,
  placeholder,
  register,
  error,
  required,
}: FieldRowProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? <RequiredMark /> : null}
      </Label>
      <Input
        id={id}
        type={type}
        step={step}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
        aria-required={required}
        {...register}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ id: string; name: string }>;
  error?: string;
  required?: boolean;
}

function SelectField({ id, label, value, onChange, options, error, required }: SelectFieldProps) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? <RequiredMark /> : null}
      </Label>
      {/* `''` is never a real pick — these selects have no empty option. Radix
          emits a spurious `onValueChange('')` when the controlled value moves
          from '' to the defaulted role before that item has rendered (create
          mode mounts before the role catalogs load); forwarding it validated
          the blank and flashed "Required" under both role pickers before the
          user had touched anything. */}
      <Select value={value} onValueChange={(v) => v && onChange(v)}>
        <SelectTrigger id={id} className="w-full" aria-required={required}>
          <SelectValue placeholder="Select…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

/** Trim and collapse empty strings to null — the API treats both
 *  identically and the partial-unique indexes assume null absence. */
function blank(v: string | undefined): string | null {
  if (v === undefined) return null;
  const trimmed = v.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** The address (PII) fields of a create/patch payload. Spread into the request
 *  only when the caller holds user.pii.view — see the mutation above. Kept as
 *  one helper so the create and edit payloads can't drift on the field set. */
function addressPayload(values: UserEditValues) {
  return {
    addressLine1: blank(values.addressLine1),
    addressLine2: blank(values.addressLine2),
    city: blank(values.city),
    state: blank(values.state),
    zip: blank(values.zip),
    country: blank(values.country),
    location: blank(values.location),
  };
}

/** Parse a numeric-input string; '' or invalid → null, otherwise Number(). */
function emptyNumber(v: string | undefined): number | null {
  if (v === undefined) return null;
  const trimmed = v.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** Form fields use '' for absent dates so the input renders blank. */
function emptyToNull(v: string | undefined): string | null {
  if (!v) return null;
  return v;
}
